import { Fragment, type ReactNode } from 'react'

import { F0Box, F0Button, type IconType } from '@factorialco/f0-react'

import type { GridSelection } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { useFormatBarAnchor } from 'modules/shift_management/hooks/formatBar/useFormatBarAnchor'

import { Separator } from 'modules/shift_management/components/ShiftManagement/Separator'
import TooltipWrapper from 'modules/shift_management/components/ShiftManagement/TooltipWrapper'

export interface FormatBarAction {
  icon: IconType
  label?: string
  onClick: () => void
  disabled?: boolean
}

interface Props {
  children: ReactNode
  actions: FormatBarAction[]
  visible: boolean
  selection: GridSelection
  gridRoot: HTMLDivElement | null
  secondaryButton?: ReactNode
}

const FormatBar = ({ children, actions, visible, selection, gridRoot, secondaryButton }: Props) => {
  const anchor = useFormatBarAnchor({ visible, selection, gridRoot })

  if (!visible || !anchor) return null

  return (
    <div
      role="dialog"
      className="dark"
      style={{
        // using div to positionate the outside box
        top: `${anchor.top}px`,
        left: `${anchor.left}px`,
        transform: 'translate(-50%, -100%)',
        zIndex: '5',
        position: 'fixed',
        display: !anchor ? 'none' : undefined,
      }}
    >
      <F0Box
        data-testid="format-bar-test"
        alignItems="center"
        padding="sm"
        gap="sm"
        flexDirection="row"
        background="primary"
        width="full"
        maxWidth="80"
        display="flex"
        borderRadius="lg"
      >
        <F0Box width="full" maxWidth="48" minWidth="32">
          {children}
        </F0Box>
        {actions.map((action, index) => (
          <Fragment key={`${index}-${action.label ?? 'format-action'}`}>
            {index === 1 && <Separator />}
            <TooltipWrapper.Alt label={action.label ?? ''}>
              <F0Button
                hideLabel
                icon={action.icon}
                label={action.label ?? ''}
                onClick={action.onClick}
                size="md"
                variant="outline"
                disabled={action.disabled}
              />
            </TooltipWrapper.Alt>
          </Fragment>
        ))}
        {secondaryButton && secondaryButton}
      </F0Box>
    </div>
  )
}

export { FormatBar }
